// Fixed grid rows cannot grow for text, so text longer than its rows runs into the items below.
// An item marked data-keep-clear pushes the items on its row and below down, together, by what its
// content would otherwise run into. On the phone grid every item's text is also kept clear of later
// rows, a row at a time so an image stays level with its text, since copy edited in the admin can
// grow at any time; desktop compositions overlap on purpose, so there only marked items count.
(() => {
  if (window.dynamicGridKeepClear) return;
  window.dynamicGridKeepClear = true;

  const gap = 32;
  const desktop = window.matchMedia('(min-width: 1000px)');

  const shiftOf = (cell) => parseFloat(cell.style.getPropertyValue('--dynamic-grid-clear-shift')) || 0;
  const overlapsHorizontally = (a, b) => a.right > b.left && a.left < b.right;

  // Positions as they would be with no shift, so the result does not depend on the last one.
  const rectsOf = (cell, selector) => {
    const current = shiftOf(cell);

    return [...cell.querySelectorAll(selector)]
      .map((node) => node.getBoundingClientRect())
      .filter((rect) => rect.width > 0 && rect.height > 0)
      .map((rect) => ({ top: rect.top - current, bottom: rect.bottom - current, left: rect.left, right: rect.right }));
  };

  const update = (grid) => {
    // By grid row, not page order: blocks do not render in the order the grid places them.
    const items = [...grid.querySelectorAll('.dynamic-grid__cell')]
      .filter((cell) => cell.closest('.dynamic-grid') === grid && cell.getClientRects().length > 0)
      .map((cell) => ({
        cell,
        row: parseInt(getComputedStyle(cell).gridRowStart, 10) || 0,
        marked: cell.hasAttribute('data-keep-clear'),
        content: rectsOf(cell, 'img, p, h1, h2, h3, h4, h5, h6, li'),
        text: rectsOf(cell, 'p, h1, h2, h3, h4, h5, h6, li'),
        shift: 0,
      }))
      .sort((a, b) => a.row - b.row);

    items.filter((item) => item.marked && item.content.length).forEach((kept) => {
      const after = items.filter((item) => item !== kept && item.row >= kept.row);
      const box = {
        bottom: Math.max(...kept.content.map((rect) => rect.bottom)) + kept.shift,
        left: Math.min(...kept.content.map((rect) => rect.left)),
        right: Math.max(...kept.content.map((rect) => rect.right)),
      };

      let deficit = 0;
      after.forEach((item) => item.content.forEach((rect) => {
        if (overlapsHorizontally(rect, box)) deficit = Math.max(deficit, box.bottom + gap - (rect.top + item.shift));
      }));

      after.forEach((item) => { item.shift += deficit; });
    });

    if (!desktop.matches) {
      let carried = 0;

      [...new Set(items.map((item) => item.row))].forEach((current) => {
        const inRow = items.filter((item) => item.row === current);
        const above = items.filter((item) => item.row < current);
        let needed = 0;

        // Only an actual overlap moves anything, so a design's own tighter spacing stays.
        inRow.forEach((item) => above.forEach((other) => other.text.forEach((textRect) => item.content.forEach((rect) => {
          const bottom = textRect.bottom + other.shift;
          const top = rect.top + item.shift + carried;
          if (overlapsHorizontally(rect, textRect) && bottom > top) needed = Math.max(needed, bottom + gap - top);
        }))));

        carried += needed;
        inRow.forEach((item) => { item.shift += carried; });
      });
    }

    items.forEach(({ cell, shift }) => {
      const rounded = Math.max(0, Math.round(shift));
      if (rounded === Math.round(shiftOf(cell))) return;

      if (rounded) {
        cell.style.setProperty('--dynamic-grid-clear-shift', `${rounded}px`);
      } else {
        cell.style.removeProperty('--dynamic-grid-clear-shift');
      }
    });
  };

  const grids = () => [...document.querySelectorAll('.dynamic-grid')];
  const observer = new ResizeObserver((entries) => entries.forEach((entry) => update(entry.target)));

  const setup = () => grids().forEach((grid) => { observer.observe(grid); update(grid); });

  setup();
  document.fonts?.ready.then(() => grids().forEach(update));
  desktop.addEventListener('change', () => grids().forEach(update));
  document.addEventListener('shopify:section:load', setup);
})();
